/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentThemeType } from '../../UNSAFE_Theme';
import { Logger } from '../../utils/UNSAFE_logger';

type Options = Record<string, unknown>;

/**
 * A hook for getting the classes associated with a component within the theming system
 */
const useComponentTheme = <VariantObjects extends Options, Styles = Record<string, never>>(
  componentTheme?: ComponentThemeType<unknown, unknown>,
  options?: VariantObjects
) => {
  const {
    multiVariantStyles,
    styles,
    variants = [],
    baseTheme = ''
  } = componentTheme || ({} as any);

  // Here we filter down from the keys in the options parameter that match the list of variants
  const variantsFromOptions = Object.keys(variants).reduce((acc: Options, cur: string) => {
    acc[cur] = options?.[cur];
    return acc;
  }, {});

  if (!multiVariantStyles) {
    Logger.warn(
      `You are missing a theme for your component. You may need to create one or provide a theme in your Environment context.`
    );
  }

  const variantClasses = multiVariantStyles ? multiVariantStyles(variantsFromOptions) : '';

  // returned here are the styles defined in the component theme, and the classes.
  // classes always includes the baseTheme class defined in the component theme.
  // we also append the classes returned from the multiVariantStyles function
  return {
    styles: styles as Styles,
    baseTheme,
    variantClasses,
    classes: `${baseTheme} ${variantClasses}`
  };
};

export { useComponentTheme };
